<template>
  <div id="app" class="container-fluid">
    <full_fp_head :visit_id="visit_id" />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading family planning visits...</p>
      </div>
      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>
      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-pills"></i> Family Planning Visits</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Record Visit
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">
            No family planning visits recorded yet.
          </div>
          <div v-else class="visit-list">
            <div class="visit-card" v-for="record in records" :key="record.id">
              <div class="visit-card-header">
                <div>
                  <strong>{{ record.method_given || "No method recorded" }}</strong>
                  <span v-if="record.visit_type" class="badge-site">{{ record.visit_type }}</span>
                </div>
                <div class="visit-actions">
                  <span class="visit-date">{{ formatDateTime(record.visit_date) }}</span>
                  <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
              <div class="visit-body">
                <div class="visit-row">
                  <span v-if="record.previous_method"><strong>Previous:</strong> {{ record.previous_method }}</span>
                  <span v-if="record.method_start_date"
                    ><strong>Started:</strong> {{ formatDate(record.method_start_date) }}</span
                  >
                  <span v-if="record.next_appointment_date"
                    ><strong>Next Appt:</strong> {{ formatDate(record.next_appointment_date) }}</span
                  >
                </div>
                <p v-if="record.counseling_provided" class="visit-text">
                  <strong>Counseling:</strong> {{ record.counseling_notes || "Provided" }}
                </p>
                <p v-if="record.side_effects" class="visit-text">
                  <strong>Side Effects:</strong> {{ record.side_effects }}
                </p>
                <p v-if="record.complications" class="visit-text">
                  <strong>Complications:</strong> {{ record.complications }}
                </p>
                <p v-if="record.notes" class="visit-text"><strong>Notes:</strong> {{ record.notes }}</p>
                <p v-if="record.provider" class="visit-meta">Seen by {{ record.provider }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FpVisitModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_fp_head from "./full_fp_head.vue";
import FpVisitModal from "./FpVisitModal.vue";
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "FpVisitAssessment",
  components: { full_fp_head, FpVisitModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`family_planning_records/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load family planning visits";
        }
      } catch (err) {
        console.error("Error fetching family planning visits:", err);
        this.error = "An error occurred while loading family planning visits. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this visit record?")) return;
      try {
        const response = await generalApiRequest(`family_planning_records/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete visit record");
        }
      } catch (error) {
        console.error("Error deleting family planning visit:", error);
        alert("Failed to delete visit record. Please try again.");
      }
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #97266d; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #97266d; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #97266d; }
.btn-edit-header { background: linear-gradient(135deg, #ed64a6 0%, #97266d 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.visit-list { display: flex; flex-direction: column; gap: 14px; }
.visit-card { border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden; }
.visit-card-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: #f8f9fa; border-bottom: 1px solid #e9ecef; flex-wrap: wrap; gap: 8px; }
.badge-site { margin-left: 8px; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; background: #fdf2f8; color: #97266d; }
.visit-actions { display: flex; align-items: center; gap: 8px; }
.visit-date { font-size: 12px; color: #6c757d; }
.btn-icon { background: none; border: none; color: #97266d; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
.visit-body { padding: 12px 14px; }
.visit-row { display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #495057; margin-bottom: 8px; }
.visit-text { font-size: 13px; color: #495057; margin: 4px 0; }
.visit-meta { font-size: 11px; color: #6c757d; font-style: italic; margin-top: 8px; }
</style>
